// Per-SMS-campaign detail — read-mode config by default, "Edit" toggles an
// inline form (name/vertical/callback DID + the four template arrays), plus a
// LIVE send-rate control (number + slider, debounced PATCH of msgs_per_min),
// an Active/Pause toggle, and a Run History table (newest-first).
//
// Mirrors pages/MirrorClientDetail.tsx: GET fetch on route-param change with a
// `cancelled` unmount-abort flag, inline-edit toggle with form state,
// onSave → api.patchSmsCampaign, transient "Saved." message.
//
// The send rate is a real-time operator control: editing msgs_per_min PATCHes
// immediately (debounced) so a running campaign can be throttled mid-flight —
// the dispatcher reads it fresh each tick, so changes take effect within ~60s.
// Templates are plain {n}/{p} placeholder strings (one per line in a textarea),
// never JS functions.

import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import SmsPreview from "../components/SmsPreview";
import {
  api,
  ApiError,
  SMS_RECIPIENT_UPLOAD_CAP,
  type SmsCampaignDetailResponse,
  type SmsCampaignListItem,
  type SmsCampaignPatchRequest,
  type SmsRecipientSummary,
  type SmsRecipientUploadResult,
  type SmsRollup,
  type SmsRunReport,
} from "../lib/api";
import {
  buttonStyle,
  Card,
  EmptyState,
  FieldLabel,
  inputStyle,
  PageHeader,
  Pill,
  Skeleton,
  StatCard,
  tokens,
} from "../lib/theme";

const RPS_DEBOUNCE_MS = 500;
const RPS_MIN = 1;
const RPS_MAX = 600;

// Poll interval for the auto-updating campaign performance cards + recipient
// summary (mirrors the SMS dashboard's POLL_MS). Lets the operator watch a live
// test send + its attribution update without reloading the page.
const CARD_POLL_MS = 15_000;

// Default opt-out suffix shown when a legacy campaign has no stored footer —
// mirrors the admin-api / rtb-api DEFAULT_FOOTER (buildSmsBody falls back to it).
const DEFAULT_FOOTER = "Reply STOP to stop getting messages. Thank you.";

interface EditForm {
  campaign_name: string;
  vertical: string;
  callback_did: string;
  openers: string;
  openers_no_name: string;
  actions: string;
  ctas: string;
  footer: string;
}

function toEditForm(c: SmsCampaignDetailResponse): EditForm {
  return {
    campaign_name: c.campaign_name,
    vertical: c.vertical,
    callback_did: c.callback_did,
    openers: c.openers.join("\n"),
    openers_no_name: c.openers_no_name.join("\n"),
    actions: c.actions.join("\n"),
    ctas: c.ctas.join("\n"),
    footer: c.footer && c.footer.length > 0 ? c.footer : DEFAULT_FOOTER,
  };
}

function toLines(text: string): string[] {
  return text
    .split("\n")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export default function SmsCampaignDetail() {
  const params = useParams();
  const id = params["id"] ?? "";

  const [campaign, setCampaign] =
    useState<SmsCampaignDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Inline-edit (templates + config) state.
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<EditForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  // Live send-rate control state (independent of the edit form).
  const [rps, setRps] = useState(0);
  const [rpsPending, setRpsPending] = useState(false);
  const rpsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Active/Pause toggle state.
  const [toggling, setToggling] = useState(false);

  // Run history.
  const [runs, setRuns] = useState<SmsRunReport[] | null>(null);
  const [runsError, setRunsError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    api
      .getSmsCampaign(id)
      .then((c) => {
        if (cancelled) return;
        setCampaign(c);
        setForm(toEditForm(c));
        setRps(c.msgs_per_min);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Runs drive the "Campaign performance" cards + run history. Load on mount
  // AND poll every CARD_POLL_MS so the cards auto-update (sends, attributed
  // calls, revenue) without a page reload — e.g. while watching a live test
  // send + its TrackDrive call attribution roll in. The interval is cleared on
  // unmount / id change. A poll error keeps the last good data (sets the error
  // banner but doesn't blank the cards).
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const loadRuns = (): void => {
      api
        .getCampaignRuns(id)
        .then((r) => {
          if (cancelled) return;
          const sorted = [...r.runs].sort(
            (a, b) => b.started_at_ms - a.started_at_ms,
          );
          setRuns(sorted);
          setRunsError(null);
        })
        .catch((err: unknown) => {
          if (!cancelled)
            setRunsError(err instanceof Error ? err.message : "Failed to load");
        });
    };
    loadRuns();
    const timer = setInterval(loadRuns, CARD_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [id]);

  // Clear any pending debounce timer on unmount.
  useEffect(() => {
    return () => {
      if (rpsTimer.current) clearTimeout(rpsTimer.current);
    };
  }, []);

  // Deep-link to the CSV upload step: the create flow ("Next") sends the
  // operator to /sms/campaigns/{id}#recipients so they land on the Recipient
  // list card. Once the campaign has loaded (so the #recipients node exists),
  // scroll it into view. Runs only when the hash is present.
  useEffect(() => {
    if (loading || !campaign) return;
    if (window.location.hash !== "#recipients") return;
    const el = document.getElementById("recipients");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [loading, campaign]);

  // Merge a patched campaign (list shape) back into the detail state.
  const mergeUpdated = (updated: SmsCampaignListItem): void => {
    setCampaign((prev) => (prev ? { ...prev, ...updated } : prev));
  };

  // Live send-rate: update local value immediately, debounce the PATCH.
  const onChangeRps = (next: number): void => {
    const clamped = Math.max(RPS_MIN, Math.min(RPS_MAX, Math.round(next)));
    setRps(clamped);
    if (rpsTimer.current) clearTimeout(rpsTimer.current);
    rpsTimer.current = setTimeout(() => {
      void commitRps(clamped);
    }, RPS_DEBOUNCE_MS);
  };

  const commitRps = async (value: number): Promise<void> => {
    if (!id) return;
    setRpsPending(true);
    try {
      const updated = await api.patchSmsCampaign(id, { msgs_per_min: value });
      mergeUpdated(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update rate");
    } finally {
      setRpsPending(false);
    }
  };

  // Active/Pause toggle — separate from the edit form, debounced via `toggling`.
  const onToggleState = async (): Promise<void> => {
    if (!id || !campaign || toggling) return;
    const next = campaign.state === "active" ? "paused" : "active";
    setToggling(true);
    setError(null);
    try {
      const updated = await api.patchSmsCampaign(id, { state: next });
      mergeUpdated(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to change state");
    } finally {
      setToggling(false);
    }
  };

  const onSave = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!id || !form) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      const body: SmsCampaignPatchRequest = {
        campaign_name: form.campaign_name.trim(),
        vertical: form.vertical.trim(),
        callback_did: form.callback_did.trim(),
        openers: toLines(form.openers),
        openers_no_name: toLines(form.openers_no_name),
        actions: toLines(form.actions),
        ctas: toLines(form.ctas),
        footer: form.footer.trim() || DEFAULT_FOOTER,
      };
      const updated = await api.patchSmsCampaign(id, body);
      mergeUpdated(updated);
      setCampaign((prev) => (prev ? { ...prev, ...body } : prev));
      setSaveMessage("Saved.");
      setEditing(false);
      setTimeout(() => setSaveMessage(null), 2400);
    } catch (err) {
      setSaveMessage(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onCancelEdit = (): void => {
    if (!campaign) return;
    setForm(toEditForm(campaign));
    setEditing(false);
    setSaveMessage(null);
  };

  if (loading && !campaign) {
    return (
      <div>
        <PageHeader title={<Skeleton width={220} height={26} />} />
        <Skeleton height={140} />
      </div>
    );
  }

  if (error && !campaign) {
    return (
      <Card>
        <EmptyState title="Failed to load campaign" hint={error} />
      </Card>
    );
  }

  if (!campaign || !form) {
    return (
      <Card>
        <EmptyState title="Campaign not found" />
      </Card>
    );
  }

  const isActive = campaign.state === "active";

  return (
    <div>
      <PageHeader
        title={campaign.campaign_name}
        subtitle={
          <span style={{ fontFamily: tokens.font.mono }}>
            {campaign.campaign_id}
          </span>
        }
        actions={
          <div
            style={{ display: "flex", alignItems: "center", gap: tokens.space.sm }}
          >
            <Pill tone={isActive ? "success" : "neutral"}>
              {isActive ? "Active" : "Paused"}
            </Pill>
            <button
              type="button"
              onClick={() => void onToggleState()}
              disabled={toggling}
              style={buttonStyle(isActive ? "ghost" : "primary", toggling)}
            >
              {toggling
                ? "Changing…"
                : isActive
                  ? "Pause campaign"
                  : "Activate campaign"}
            </button>
          </div>
        }
      />

      {/* ───────────── Real-Time Send Rate ───────────── */}
      <Card title="Real-Time Send Rate" style={{ marginBottom: tokens.space.xl }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: tokens.space.lg,
            flexWrap: "wrap",
          }}
        >
          <div style={{ minWidth: 120 }}>
            <FieldLabel htmlFor="sms-rps-number" hint="messages per minute">
              Msgs/min
            </FieldLabel>
            <input
              id="sms-rps-number"
              type="number"
              min={RPS_MIN}
              max={RPS_MAX}
              value={rps}
              onChange={(e) => onChangeRps(Number(e.target.value))}
              style={{ ...inputStyle, width: 110 }}
            />
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <input
              id="sms-rps-slider"
              type="range"
              min={RPS_MIN}
              max={RPS_MAX}
              value={rps}
              onChange={(e) => onChangeRps(Number(e.target.value))}
              style={{ width: "100%" }}
              aria-label="Send rate (messages per minute)"
            />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                color: tokens.color.fg2,
                fontSize: tokens.font.size.xs,
                fontFamily: tokens.font.mono,
              }}
            >
              <span>{RPS_MIN}</span>
              <span>{RPS_MAX}</span>
            </div>
          </div>
          <div style={{ minWidth: 90 }}>
            <span
              style={{
                color: rpsPending ? tokens.color.warning : tokens.color.fg2,
                fontSize: tokens.font.size.sm,
              }}
            >
              {rpsPending ? "Updating…" : "Live"}
            </span>
          </div>
        </div>
        <p
          style={{
            margin: `${tokens.space.md}px 0 0`,
            color: tokens.color.fg1,
            fontSize: tokens.font.size.sm,
          }}
        >
          Increasing this raises SMS sends per minute, which increases inbound
          call volume proportionally. Decreasing it reduces inbound calls. The
          change applies on the next dispatch tick (within ~60s) — no redeploy
          needed.
        </p>
      </Card>

      {/* ───────────── Configuration ───────────── */}
      <Card
        title="Configuration"
        action={
          editing ? null : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              style={buttonStyle("ghost")}
            >
              Edit
            </button>
          )
        }
        style={{ marginBottom: tokens.space.xl }}
      >
        {editing ? (
          <form onSubmit={onSave}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: tokens.space.lg,
              }}
            >
              <div>
                <FieldLabel htmlFor="scfg-name">Campaign name</FieldLabel>
                <input
                  id="scfg-name"
                  value={form.campaign_name}
                  onChange={(e) =>
                    setForm((f) =>
                      f ? { ...f, campaign_name: e.target.value } : f,
                    )
                  }
                  style={inputStyle}
                />
              </div>
              <div>
                <FieldLabel htmlFor="scfg-vertical">Vertical</FieldLabel>
                <input
                  id="scfg-vertical"
                  value={form.vertical}
                  onChange={(e) =>
                    setForm((f) => (f ? { ...f, vertical: e.target.value } : f))
                  }
                  style={inputStyle}
                />
              </div>
              <div>
                <FieldLabel htmlFor="scfg-did">Callback DID</FieldLabel>
                <input
                  id="scfg-did"
                  value={form.callback_did}
                  onChange={(e) =>
                    setForm((f) =>
                      f ? { ...f, callback_did: e.target.value } : f,
                    )
                  }
                  style={inputStyle}
                />
              </div>
            </div>

            <p
              style={{
                color: tokens.color.fg2,
                fontSize: tokens.font.size.sm,
                margin: `${tokens.space.lg}px 0 ${tokens.space.md}px`,
              }}
            >
              Templates: one variant per line.{" "}
              <code style={{ fontFamily: tokens.font.mono }}>{"{n}"}</code> =
              lead first name,{" "}
              <code style={{ fontFamily: tokens.font.mono }}>{"{p}"}</code> =
              callback DID.
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: tokens.space.lg,
              }}
            >
              <TemplateField
                id="scfg-openers"
                label="Openers (with name)"
                value={form.openers}
                onChange={(v) =>
                  setForm((f) => (f ? { ...f, openers: v } : f))
                }
              />
              <TemplateField
                id="scfg-openers-no-name"
                label="Openers (no name)"
                value={form.openers_no_name}
                onChange={(v) =>
                  setForm((f) => (f ? { ...f, openers_no_name: v } : f))
                }
              />
              <TemplateField
                id="scfg-actions"
                label="Actions"
                value={form.actions}
                onChange={(v) =>
                  setForm((f) => (f ? { ...f, actions: v } : f))
                }
              />
              <TemplateField
                id="scfg-ctas"
                label="CTAs"
                value={form.ctas}
                onChange={(v) => setForm((f) => (f ? { ...f, ctas: v } : f))}
              />
            </div>

            <div style={{ marginTop: tokens.space.lg }}>
              <FieldLabel
                htmlFor="scfg-footer"
                hint="appended to every message (plain text — no {n}/{p})"
              >
                Footer / opt-out suffix
              </FieldLabel>
              <input
                id="scfg-footer"
                value={form.footer}
                maxLength={160}
                onChange={(e) =>
                  setForm((f) => (f ? { ...f, footer: e.target.value } : f))
                }
                style={inputStyle}
              />
            </div>

            {/* Live preview of a real composed message from the current copy. */}
            <SmsPreview
              openers={toLines(form.openers)}
              openers_no_name={toLines(form.openers_no_name)}
              actions={toLines(form.actions)}
              ctas={toLines(form.ctas)}
              callback_did={form.callback_did}
              footer={form.footer}
            />

            <div
              style={{
                marginTop: tokens.space.lg,
                display: "flex",
                gap: tokens.space.sm,
                alignItems: "center",
              }}
            >
              <button
                type="submit"
                disabled={saving}
                style={buttonStyle("primary", saving)}
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
              <button
                type="button"
                onClick={onCancelEdit}
                disabled={saving}
                style={buttonStyle("ghost", saving)}
              >
                Cancel
              </button>
              {saveMessage && (
                <span
                  style={{
                    color: tokens.color.fg1,
                    fontSize: tokens.font.size.sm,
                  }}
                >
                  {saveMessage}
                </span>
              )}
            </div>
          </form>
        ) : (
          <ReadOnlyConfig campaign={campaign} />
        )}
      </Card>

      {/* ───────── Per-campaign performance (calls + revenue) ─────────
          Same KPIs as the SMS dashboard, but scoped to THIS campaign — summed
          from the run-history rollups already fetched above (no extra fetch). */}
      <CampaignStatsCards runs={runs} error={runsError} />

      {/* ───────────── Recipient list (list campaigns only) ───────────── */}
      {/* #recipients anchor — the create flow deep-links here after "Next" so the
          operator lands on the CSV upload step (see the hash-scroll effect above). */}
      {campaign.lead_source === "list" && (
        <div id="recipients">
          <RecipientList id={id} />
        </div>
      )}

      {/* ───────────── Run History ───────────── */}
      <Card title="Run history">
        <RunHistory runs={runs} error={runsError} />
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-campaign performance cards — the same KPIs as the SMS dashboard totals,
// but scoped to THIS campaign. Computed by summing the run-history rollups
// already fetched for the Run history table (no extra API call). Shown above
// the recipient list so the operator sees calls + revenue for the numbers this
// campaign sent.
// ---------------------------------------------------------------------------

// Campaign-card formatters. NOTE the existing fmtUsd lower in this file takes
// DOLLARS; these take the rollup's native units (revenueCents = cents,
// rates = fractions), matching SmsRollup + the dashboard cards.
function fmtStatCount(n: number): string {
  return (n ?? 0).toLocaleString();
}
function fmtStatUsdFromCents(cents: number): string {
  return `$${((cents ?? 0) / 100).toFixed(2)}`;
}
function fmtStatPct(n: number): string {
  return `${((n ?? 0) * 100).toFixed(1)}%`;
}

/** Sum a set of run rollups into one campaign-total rollup. Rates are derived
 *  from the summed counts (denominator = delivered), NOT averaged across runs. */
function sumRollups(runs: SmsRunReport[]): SmsRollup {
  const t = runs.reduce(
    (acc, r) => {
      const ro = r.rollup;
      acc.sends += ro.sends;
      acc.delivered += ro.delivered;
      acc.failed += ro.failed;
      acc.skipped += ro.skipped;
      acc.attributedCalls += ro.attributedCalls;
      acc.conversions += ro.conversions;
      acc.revenueCents += ro.revenueCents;
      return acc;
    },
    {
      sends: 0,
      delivered: 0,
      failed: 0,
      skipped: 0,
      attributedCalls: 0,
      conversions: 0,
      revenueCents: 0,
    },
  );
  const denom = t.delivered || 0;
  return {
    ...t,
    callRate: denom ? t.attributedCalls / denom : 0,
    conversionRate: denom ? t.conversions / denom : 0,
    revenuePerSend: denom ? t.revenueCents / denom : 0,
  };
}

function CampaignStatsCards({
  runs,
  error,
}: {
  runs: SmsRunReport[] | null;
  error: string | null;
}) {
  // Loading: runs not yet fetched. Keep it quiet (the Run history card below
  // shows its own state); render a slim skeleton row.
  if (runs === null) {
    return (
      <Card title="Campaign performance" style={{ marginBottom: tokens.space.xl }}>
        <Skeleton height={88} />
      </Card>
    );
  }
  const t = sumRollups(runs);
  return (
    <Card title="Campaign performance" style={{ marginBottom: tokens.space.xl }}>
      {error && (
        <p
          style={{
            margin: `0 0 ${tokens.space.md}px`,
            color: tokens.color.fg2,
            fontSize: tokens.font.size.sm,
          }}
        >
          {error} — showing the last successful load.
        </p>
      )}
      {runs.length === 0 ? (
        <EmptyState
          title="No sends yet"
          hint="Performance appears here once this campaign has sent its first run."
        />
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: tokens.space.md,
          }}
        >
          <StatCard label="Sent (delivered)" value={fmtStatCount(t.delivered)} hint="messages delivered to carrier" emphasis />
          <StatCard label="Send attempts" value={fmtStatCount(t.sends)} hint="total send attempts (incl. failed + skipped)" />
          <StatCard label="Attributed Calls" value={fmtStatCount(t.attributedCalls)} hint="calls after a send" emphasis />
          <StatCard label="Revenue" value={fmtStatUsdFromCents(t.revenueCents)} hint="attributed revenue" emphasis />
          <StatCard label="Call Rate" value={fmtStatPct(t.callRate)} hint="calls / delivered" />
          <StatCard label="Conversions" value={fmtStatCount(t.conversions)} hint="conversions attributed" />
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Recipient list (uploaded-list campaigns only) — CSV upload + status summary
// ---------------------------------------------------------------------------

// Guard against accidentally uploading a binary/huge non-CSV blob: cap the
// number of bytes we read into memory. 100k rows ≈ a few MB; 16MB is generous.
const MAX_CSV_BYTES = 16 * 1024 * 1024;

/** Cheap client-side sanity check that a File looks like CSV: a .csv/.txt
 *  extension or a text/CSV-ish MIME type. We only reject the obviously-wrong
 *  cases — the backend is the authority on row validity. */
function looksLikeCsvFile(file: File): boolean {
  const name = file.name.toLowerCase();
  if (name.endsWith(".csv") || name.endsWith(".txt")) return true;
  const type = file.type.toLowerCase();
  return type.includes("csv") || type === "text/plain";
}

function RecipientList({ id }: { id: string }) {
  const [summary, setSummary] = useState<SmsRecipientSummary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(true);

  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] =
    useState<SmsRecipientUploadResult | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Initial load (+ reload on id change) AND poll every CARD_POLL_MS so the
  // recipient status counts auto-update without a page reload (a poll keeps the
  // last good data on error). cancelled-flag unmount guard + interval cleanup.
  useEffect(() => {
    let cancelled = false;
    const load = (showSpinner: boolean): void => {
      if (showSpinner) setLoadingSummary(true);
      api
        .getSmsRecipientSummary(id)
        .then((s) => {
          if (cancelled) return;
          setSummary(s);
          setSummaryError(null);
        })
        .catch((err: unknown) => {
          if (!cancelled)
            setSummaryError(
              err instanceof Error ? err.message : "Failed to load",
            );
        })
        .finally(() => {
          if (!cancelled && showSpinner) setLoadingSummary(false);
        });
    };
    load(true);
    const timer = setInterval(() => load(false), CARD_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [id]);

  // Refetch the summary on demand (after an upload). No unmount guard needed —
  // it's only invoked from a live event handler.
  const refreshSummary = (): void => {
    setLoadingSummary(true);
    api
      .getSmsRecipientSummary(id)
      .then((s) => {
        setSummary(s);
        setSummaryError(null);
      })
      .catch((err: unknown) => {
        setSummaryError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => {
        setLoadingSummary(false);
      });
  };

  const onPickFile = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = e.target.files?.[0];
    // Allow re-selecting the same file later (onChange won't fire otherwise).
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;

    setUploadResult(null);
    setUploadError(null);

    if (!looksLikeCsvFile(file)) {
      setUploadError("Please choose a .csv file.");
      return;
    }
    if (file.size > MAX_CSV_BYTES) {
      setUploadError(
        `File is too large (max ${(MAX_CSV_BYTES / (1024 * 1024)).toFixed(0)}MB).`,
      );
      return;
    }

    setUploading(true);
    try {
      const text = await file.text();
      if (text.trim().length === 0) {
        setUploadError("The file is empty.");
        return;
      }
      const result = await api.uploadSmsRecipients(id, text);
      setUploadResult(result);
      // Refresh the status summary so the counts reflect the new rows.
      refreshSummary();
    } catch (err) {
      // Surface the backend's parse/validation message (ApiError carries it).
      if (err instanceof ApiError) {
        setUploadError(err.message);
      } else {
        setUploadError(err instanceof Error ? err.message : "Upload failed");
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card title="Recipient list" style={{ marginBottom: tokens.space.xl }}>
      <p
        style={{
          margin: `0 0 ${tokens.space.lg}px`,
          color: tokens.color.fg1,
          fontSize: tokens.font.size.sm,
        }}
      >
        This campaign texts only the recipients you upload here (not the live
        credit cache). Upload a CSV to add recipients —{" "}
        <strong style={{ color: tokens.color.fg0 }}>
          CSV columns: number (+1 format), first_name
        </strong>
        . A leading header row is detected and skipped automatically. Up to{" "}
        {SMS_RECIPIENT_UPLOAD_CAP.toLocaleString()} recipients per upload (extra
        rows are dropped).
      </p>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: tokens.space.md,
          flexWrap: "wrap",
        }}
      >
        <label htmlFor="sms-recipients-file" style={buttonStyle("primary", uploading)}>
          {uploading ? "Uploading…" : "Upload CSV"}
        </label>
        <input
          ref={fileInputRef}
          id="sms-recipients-file"
          type="file"
          accept=".csv,text/csv"
          disabled={uploading}
          onChange={(e) => void onPickFile(e)}
          style={{ display: "none" }}
        />
        <span
          style={{
            color: tokens.color.fg2,
            fontSize: tokens.font.size.xs,
            fontFamily: tokens.font.mono,
          }}
        >
          number,first_name
        </span>
      </div>

      {uploadError && (
        <div
          role="alert"
          style={{
            marginTop: tokens.space.lg,
            padding: tokens.space.md,
            background: tokens.color.dangerBg,
            border: `1px solid ${tokens.color.dangerBorder}`,
            color: tokens.color.danger,
            borderRadius: tokens.radius.sm,
            fontSize: tokens.font.size.sm,
          }}
        >
          {uploadError}
        </div>
      )}

      {uploadResult && (
        <div
          style={{
            marginTop: tokens.space.lg,
            padding: tokens.space.md,
            background: "rgba(0,212,170,0.06)",
            border: "1px solid rgba(0,212,170,0.30)",
            borderRadius: tokens.radius.sm,
            color: tokens.color.fg1,
            fontSize: tokens.font.size.sm,
          }}
        >
          Uploaded:{" "}
          <strong style={{ color: tokens.color.tealLight }}>
            {uploadResult.accepted.toLocaleString()}
          </strong>{" "}
          accepted,{" "}
          <strong style={{ color: tokens.color.fg0 }}>
            {uploadResult.rejected.toLocaleString()}
          </strong>{" "}
          rejected of{" "}
          <strong style={{ color: tokens.color.fg0 }}>
            {uploadResult.total.toLocaleString()}
          </strong>{" "}
          rows.
          {uploadResult.truncated && (
            <span style={{ color: tokens.color.warning }}>
              {" "}
              The {SMS_RECIPIENT_UPLOAD_CAP.toLocaleString()}-row cap was hit —
              some rows were dropped.
            </span>
          )}
        </div>
      )}

      <div style={{ marginTop: tokens.space.xl }}>
        <div
          style={{
            fontSize: tokens.font.size.xs,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: tokens.color.fg1,
            fontWeight: 600,
            marginBottom: tokens.space.md,
          }}
        >
          Recipient summary
        </div>
        <RecipientSummaryTable
          summary={summary}
          error={summaryError}
          loading={loadingSummary}
        />
      </div>
    </Card>
  );
}

function RecipientSummaryTable({
  summary,
  error,
  loading,
}: {
  summary: SmsRecipientSummary | null;
  error: string | null;
  loading: boolean;
}) {
  if (error && !summary) {
    return <EmptyState title="Failed to load recipient summary" hint={error} />;
  }
  if (loading && !summary) {
    return <Skeleton height={48} />;
  }
  if (!summary) {
    return (
      <EmptyState
        title="No recipients yet"
        hint="Upload a CSV to populate this list."
      />
    );
  }
  if (summary.total === 0) {
    return (
      <EmptyState
        title="No recipients yet"
        hint="Upload a CSV to populate this list."
      />
    );
  }
  const rows: Array<{ label: string; value: number }> = [
    { label: "Pending", value: summary.pending },
    { label: "Sent", value: summary.sent },
    { label: "Failed", value: summary.failed },
    { label: "Skipped", value: summary.skipped },
    { label: "Total", value: summary.total },
  ];
  return (
    <div style={{ overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "separate",
          borderSpacing: 0,
          fontSize: tokens.font.size.base,
        }}
      >
        <thead>
          <tr>
            <Th>Status</Th>
            <Th align="right">Recipients</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr
              key={r.label}
              style={{
                background:
                  idx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)",
              }}
            >
              <Td>{r.label}</Td>
              <Td align="right" mono>
                {r.value.toLocaleString()}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Read-only configuration (default state)
// ---------------------------------------------------------------------------

function ReadOnlyConfig({
  campaign,
}: {
  campaign: SmsCampaignDetailResponse;
}) {
  return (
    <div>
      <dl
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: tokens.space.lg,
          margin: `0 0 ${tokens.space.lg}px`,
        }}
      >
        <ReadField label="Vertical">{campaign.vertical}</ReadField>
        <ReadField label="Lead source">
          {campaign.lead_source === "list"
            ? "Uploaded list"
            : "Live cache leads"}
        </ReadField>
        <ReadField label="State">
          <Pill tone={campaign.state === "active" ? "success" : "neutral"}>
            {campaign.state === "active" ? "Active" : "Paused"}
          </Pill>
        </ReadField>
        <ReadField label="Callback DID" mono>
          {campaign.callback_did}
        </ReadField>
        <ReadField label="Resend guard (ms)" mono>
          {campaign.resend_guard_ms}
        </ReadField>
        <ReadField label="Footer / opt-out suffix">
          {campaign.footer && campaign.footer.length > 0
            ? campaign.footer
            : DEFAULT_FOOTER}
        </ReadField>
      </dl>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: tokens.space.lg,
        }}
      >
        <TemplateRead label="Openers (with name)" lines={campaign.openers} />
        <TemplateRead
          label="Openers (no name)"
          lines={campaign.openers_no_name}
        />
        <TemplateRead label="Actions" lines={campaign.actions} />
        <TemplateRead label="CTAs" lines={campaign.ctas} />
      </div>

      {/* Live preview of a real composed message from this campaign's saved copy
          — always available (read mode), not just while editing. */}
      <SmsPreview
        openers={campaign.openers}
        openers_no_name={campaign.openers_no_name}
        actions={campaign.actions}
        ctas={campaign.ctas}
        callback_did={campaign.callback_did}
        footer={
          campaign.footer && campaign.footer.length > 0
            ? campaign.footer
            : DEFAULT_FOOTER
        }
      />
    </div>
  );
}

function ReadField({
  label,
  mono = false,
  children,
}: {
  label: string;
  mono?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt
        style={{
          fontSize: tokens.font.size.xs,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: tokens.color.fg1,
          fontWeight: 600,
          marginBottom: tokens.space.xs,
        }}
      >
        {label}
      </dt>
      <dd
        style={{
          margin: 0,
          color: tokens.color.fg0,
          fontSize: tokens.font.size.lg,
          fontWeight: 600,
          ...(mono
            ? {
                fontFamily: tokens.font.mono,
                fontVariantNumeric: "tabular-nums",
              }
            : {}),
        }}
      >
        {children}
      </dd>
    </div>
  );
}

function TemplateRead({ label, lines }: { label: string; lines: string[] }) {
  return (
    <div>
      <div
        style={{
          fontSize: tokens.font.size.xs,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: tokens.color.fg1,
          fontWeight: 600,
          marginBottom: tokens.space.xs,
        }}
      >
        {label}
      </div>
      <pre
        style={{
          margin: 0,
          padding: "10px 12px",
          background: tokens.color.bg0,
          color: tokens.color.fg1,
          borderRadius: tokens.radius.sm,
          fontFamily: tokens.font.mono,
          fontSize: tokens.font.size.sm,
          lineHeight: 1.5,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {lines.length > 0 ? lines.join("\n") : "—"}
      </pre>
    </div>
  );
}

function TemplateField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <textarea
        id={id}
        rows={5}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          ...inputStyle,
          resize: "vertical",
          fontFamily: tokens.font.mono,
          fontSize: tokens.font.size.sm,
          lineHeight: 1.5,
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Run History table (newest-first)
// ---------------------------------------------------------------------------

function RunHistory({
  runs,
  error,
}: {
  runs: SmsRunReport[] | null;
  error: string | null;
}) {
  if (error && !runs) {
    return <EmptyState title="Failed to load run history" hint={error} />;
  }
  if (!runs) {
    return <Skeleton height={48} />;
  }
  if (runs.length === 0) {
    return (
      <EmptyState
        title="No runs yet"
        hint="Runs appear once the campaign goes Active."
      />
    );
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "separate",
          borderSpacing: 0,
          fontSize: tokens.font.size.base,
        }}
      >
        <thead>
          <tr>
            <Th>Started</Th>
            <Th>Ended</Th>
            <Th align="right">Msgs/min</Th>
            <Th align="right">Sends</Th>
            <Th align="right">Calls</Th>
            <Th align="right">Call rate</Th>
            <Th align="right">Revenue</Th>
            <Th align="right">Revenue/send</Th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run, idx) => (
            <tr
              key={run.run_id}
              style={{
                background:
                  idx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)",
              }}
            >
              <Td>{fmtTs(run.started_at_ms)}</Td>
              <Td>
                {run.ended_at_ms ? (
                  fmtTs(run.ended_at_ms)
                ) : (
                  <Pill tone="success">Running</Pill>
                )}
              </Td>
              <Td align="right" mono>
                {run.msgs_per_min_at_start}
              </Td>
              <Td align="right" mono>
                {run.rollup.sends.toLocaleString()}
              </Td>
              <Td align="right" mono>
                {run.rollup.attributedCalls.toLocaleString()}
              </Td>
              <Td align="right" mono>
                {fmtPct(run.rollup.callRate)}
              </Td>
              <Td align="right" mono>
                {fmtUsd(run.rollup.revenueCents / 100)}
              </Td>
              <Td align="right" mono>
                {fmtUsd(run.rollup.revenuePerSend)}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      style={{
        textAlign: align,
        padding: `${tokens.space.md}px ${tokens.space.lg}px`,
        borderBottom: `1px solid ${tokens.color.border}`,
        color: tokens.color.fg1,
        fontSize: tokens.font.size.xs,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = "left",
  mono = false,
}: {
  children: React.ReactNode;
  align?: "left" | "right";
  mono?: boolean;
}) {
  return (
    <td
      style={{
        textAlign: align,
        padding: `${tokens.space.md}px ${tokens.space.lg}px`,
        borderBottom: `1px solid ${tokens.color.border}`,
        color: tokens.color.fg0,
        whiteSpace: "nowrap",
        ...(mono
          ? {
              fontFamily: tokens.font.mono,
              fontVariantNumeric: "tabular-nums",
            }
          : {}),
      }}
    >
      {children}
    </td>
  );
}

function fmtTs(ms: number): string {
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return String(ms);
  const now = Date.now();
  const delta = now - d.getTime();
  if (delta >= 0 && delta < 60_000) return "just now";
  if (delta >= 0 && delta < 3_600_000)
    return `${Math.floor(delta / 60_000)}m ago`;
  if (delta >= 0 && delta < 86_400_000)
    return `${Math.floor(delta / 3_600_000)}h ago`;
  return d.toLocaleString();
}

function fmtPct(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return `${(n * 100).toFixed(1)}%`;
}

function fmtUsd(dollars: number): string {
  if (Number.isNaN(dollars)) return "—";
  return `$${dollars.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
